import React, { useEffect } from "react";
import { createTheme } from "@mui/material/styles";
import { FONT_BODY, FONT_DISPLAY } from "../../utils/designTokens";

/**
 * The partner sign-up page's look: design tokens, the MUI theme its screens
 * use (inside a ThemeProvider, so the site header and footer keep theirs),
 * the stroke icons, and the fonts. Visual only.
 */

export const T = {
  red: "#C8321F",
  redHover: "#AE2A18",
  redText: "#A8291A",
  redTextStrong: "#9E2614",
  link: "#B12A18",
  linkHover: "#8E2010",
  redTint: "#FCEEEA",
  heroBg: "#FBEDE8",
  heroBorder: "#F2DCD3",
  tagBorder: "#F0D5CB",
  pageBg: "#FAF7F4",
  card: "#FFFFFF",
  cardBorder: "#ECE5DD",
  divider: "#F0EAE3",
  input: "#D6CDC3",
  inputHover: "#B9AFA4",
  fill: "#F7F2ED",
  footerBg: "#FCFAF8",
  text: "#1C1917",
  text2: "#4A443E",
  muted: "#5C554E",
  hint: "#6B635B",
  placeholder: "#8A8279",
  green: "#2E6B36",
  greenTint: "#EAF3EA",
  circle: "#CFC6BC",
  track: "#F1EBE5",
  neutralBadgeBg: "#F1EDE8",
  neutralBadgeText: "#57504A",
  groupBorder: "#E2CFC5",
  groupBg: "#FDF8F5",
  disabledBg: "#E9E3DD",
  disabledText: "#7A7169",
  infoBg: "#EEF3FB",
  infoBorder: "#D3DFF2",
  infoText: "#1E3A66",
  error: "#B3261E",
  focusRing: "0 0 0 4px rgba(200,50,31,.14)",
  // The website's two fonts (utils/designTokens.js).
  display: FONT_DISPLAY,
  body: FONT_BODY,
};

/** The same tokens as CSS variables, set on the page wrapper. */
export const cssVars = {
  "--rmf-red": T.red,
  "--rmf-red-hover": T.redHover,
  "--rmf-red-tint": T.redTint,
  "--rmf-page-bg": T.pageBg,
  "--rmf-card-border": T.cardBorder,
  "--rmf-divider": T.divider,
  "--rmf-text": T.text,
  "--rmf-text-2": T.text2,
  "--rmf-muted": T.muted,
  "--rmf-hint": T.hint,
  "--rmf-green": T.green,
  "--rmf-focus-ring": T.focusRing,
  "--rmf-display": T.display,
  "--rmf-body": T.body,
  // Card padding; the footer bar bleeds to the card edges with it.
  "--rmf-card-pad": "20px",
  "@media (min-width: 760px)": { "--rmf-card-pad": "34px" },
};

// The same stylesheet public/index.html loads, so on the website this finds
// it already there and adds nothing.
const FONT_HREF = "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,600;12..96,700;12..96,800&family=Inter:wght@400;500;600;700&display=swap";

/** Loads Bricolage Grotesque and Inter once. */
export function useSignupFonts() {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);
}

// ── Icons: inline stroke SVGs, 2px, round caps ────────────────────────────────

function Svg({ size = 20, color = "currentColor", children, title, style }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden={title ? undefined : "true"} role={title ? "img" : undefined}
      focusable="false" style={{ flexShrink: 0, ...style }}
    >
      {title && <title>{title}</title>}
      {children}
    </svg>
  );
}

export const Icon = {
  Check: (p) => <Svg {...p}><path d="M20 6 9 17l-5-5" /></Svg>,
  ArrowRight: (p) => <Svg {...p}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></Svg>,
  ArrowLeft: (p) => <Svg {...p}><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></Svg>,
  ChevronDown: (p) => <Svg {...p}><path d="m6 9 6 6 6-6" /></Svg>,
  Info: (p) => <Svg {...p}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></Svg>,
  Alert: (p) => <Svg {...p}><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></Svg>,
  External: (p) => <Svg {...p}><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></Svg>,
  Lock: (p) => <Svg {...p}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></Svg>,
  Upload: (p) => <Svg {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m17 8-5-5-5 5" /><path d="M12 3v12" /></Svg>,
  CloudCheck: (p) => <Svg {...p}><path d="M17.5 19H8a5 5 0 1 1 1.6-9.74A6 6 0 0 1 21 12.5a4.5 4.5 0 0 1-3.5 6.5Z" /><path d="m9.5 14 2 2 4-4" /></Svg>,
  File: (p) => <Svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /></Svg>,
  FileText: (p) => <Svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h5" /></Svg>,
  Bank: (p) => <Svg {...p}><path d="M3 21h18" /><path d="M5 21V10" /><path d="M19 21V10" /><path d="M9 21v-7" /><path d="M15 21v-7" /><path d="m2 10 10-6 10 6" /></Svg>,
  IdCard: (p) => <Svg {...p}><rect x="2" y="5" width="20" height="14" rx="2" /><circle cx="8" cy="12" r="2" /><path d="M5.5 16c.6-1.2 1.5-1.8 2.5-1.8s1.9.6 2.5 1.8" /><path d="M14 10h4" /><path d="M14 14h4" /></Svg>,
  Building: (p) => <Svg {...p}><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01" /></Svg>,
  Menu: (p) => <Svg {...p}><path d="M3 2v7a3 3 0 0 0 6 0V2" /><path d="M6 2v20" /><path d="M21 15V2a5 5 0 0 0-5 5v6h5" /><path d="M21 15v7" /></Svg>,
  Phone: (p) => <Svg {...p}><rect x="6" y="2" width="12" height="20" rx="2" /><path d="M11 18h2" /></Svg>,
  Camera: (p) => <Svg {...p}><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3Z" /><circle cx="12" cy="13" r="3" /></Svg>,
  Trash: (p) => <Svg {...p}><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></Svg>,
  Eye: (p) => <Svg {...p}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></Svg>,
  EyeOff: (p) => <Svg {...p}><path d="M9.9 4.24A9 9 0 0 1 12 4c6.5 0 10 8 10 8a17.6 17.6 0 0 1-2.16 3.19" /><path d="M6.6 6.6A17.6 17.6 0 0 0 2 12s3.5 8 10 8a9.7 9.7 0 0 0 5.4-1.6" /><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" /><path d="m2 2 20 20" /></Svg>,
  Plus: (p) => <Svg {...p}><path d="M12 5v14" /><path d="M5 12h14" /></Svg>,
  List: (p) => <Svg {...p}><path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" /><path d="M3 6h.01" /><path d="M3 12h.01" /><path d="M3 18h.01" /></Svg>,
  Pen: (p) => <Svg {...p}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></Svg>,
  Pin: (p) => <Svg {...p}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></Svg>,
  Mail: (p) => <Svg {...p}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></Svg>,
  User: (p) => <Svg {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" /></Svg>,
};

// Chevron for selects (appearance: none + a custom icon on the right).
function SelectChevron({ className }) {
  return (
    <span className={className} style={{ display: "inline-flex", right: 14, top: "calc(50% - 10px)", position: "absolute", pointerEvents: "none", color: T.muted }}>
      <Icon.ChevronDown size={20} />
    </span>
  );
}

const inputHeight = { padding: "12.5px 14px", fontSize: 16, lineHeight: "23px", height: "23px" };

/** The MUI theme for the sign-up screens. Breakpoint sm is 760px: one column below it. */
const signupThemeOptions = {
  breakpoints: { values: { xs: 0, sm: 760, md: 1000, lg: 1240, xl: 1536 } },
  palette: {
    primary: { main: T.red, dark: T.redHover, contrastText: "#fff" },
    error: { main: T.error },
    success: { main: T.green },
    text: { primary: T.text, secondary: T.text2 },
    background: { default: T.pageBg, paper: T.card },
    divider: T.divider,
  },
  typography: {
    fontFamily: T.body,
    fontSize: 15,
    body1: { fontSize: 16, lineHeight: 1.5 },
    body2: { fontSize: 15, lineHeight: 1.5 },
    button: { fontWeight: 600, textTransform: "none" },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: { styleOverrides: { body: { fontFamily: T.body } } },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 999, textTransform: "none", fontWeight: 600, minHeight: 44, "&.Mui-focusVisible": { boxShadow: T.focusRing } },
      },
    },
    MuiIconButton: { styleOverrides: { root: { "&.Mui-focusVisible": { boxShadow: T.focusRing } } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: "#fff",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: T.input, top: 0, "& legend": { display: "none" } },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: T.inputHover },
          "&.Mui-focused": { boxShadow: T.focusRing },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: T.red, borderWidth: 1 },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: T.error },
          "&.Mui-disabled": { background: T.fill },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: T.cardBorder },
        },
        input: {
          ...inputHeight,
          "&::placeholder": { color: T.placeholder, opacity: 1 },
          "@media (max-width: 759.95px)": { paddingTop: 13.5, paddingBottom: 13.5 },
        },
        inputSizeSmall: {
          ...inputHeight,
          "@media (max-width: 759.95px)": { paddingTop: 13.5, paddingBottom: 13.5 },
        },
        multiline: { padding: 0 },
        adornedStart: { paddingLeft: 14 },
        adornedEnd: { paddingRight: 6 },
      },
    },
    MuiSelect: {
      defaultProps: { IconComponent: SelectChevron },
      styleOverrides: { select: { appearance: "none", paddingRight: "44px !important" } },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginLeft: 0, marginRight: 0, marginTop: 6, fontSize: 13, lineHeight: 1.45, color: T.hint, "&.Mui-error": { color: T.error } },
      },
    },
    MuiCheckbox: { defaultProps: { color: "primary" }, styleOverrides: { root: { color: T.inputHover, "&.Mui-focusVisible": { boxShadow: T.focusRing, borderRadius: 6 } } } },
    MuiRadio: { defaultProps: { color: "primary" }, styleOverrides: { root: { color: T.inputHover } } },
    MuiFormControlLabel: { styleOverrides: { label: { fontSize: 15, color: T.text } } },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 16, alignItems: "flex-start", fontSize: 15 },
        standardInfo: { background: T.infoBg, border: `1px solid ${T.infoBorder}`, color: T.infoText },
        standardError: { background: "#FCEEEC", border: "1px solid #F3D3CE", color: "#7A1D14" },
        standardSuccess: { background: T.greenTint, border: "1px solid #CFE3CF", color: "#1F4D25" },
        standardWarning: { background: "#FFF6E8", border: "1px solid #F3DEB8", color: "#6B4A12" },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 999, fontWeight: 600 } } },
    MuiPaper: { styleOverrides: { rounded: { borderRadius: 20 } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 20 } } },
    MuiTableCell: { styleOverrides: { root: { borderColor: T.divider, fontSize: 15 } } },
  },
};
export const signupTheme = createTheme(signupThemeOptions);

/**
 * Inside the admin's application page the form sits next to a panel, so it
 * is narrower than a laptop screen: its two-column layout starts at 600px.
 */
export const signupFrameTheme = createTheme({
  ...signupThemeOptions,
  breakpoints: { values: { xs: 0, sm: 600, md: 900, lg: 1240, xl: 1536 } },
});

/** Step captions for the navigator, by step key. */
export const STEP_CAPTIONS = {
  business: "Restaurant, contact & address",
  legal: "As registered with the IRS",
  processing: "How you accept payments",
  owners: "Business owner details",
  bank: "Where deposits are sent",
  documents: "IDs, bank & business proof",
  terms: "Pricing and Schedule A",
  sign: "Review and e-sign",
};
